/**
 * AutoCAD Color Index (ACI) utilities for the plot pipeline
 * (docs/opencadstudio-parity/02-plot-pdf-ctb.md task 2.3).
 *
 * Yap stores entity colors as '#rrggbb'; CTB plot styles resolve per ACI.
 * The mapping is therefore: resolve the effective color (BYLAYER chain) →
 * nearest standard ACI → CTB lookup. True-color fidelity is preserved
 * when no CTB is active (the hex renders directly) — the ACI round-trip
 * only ever recolors through the plot style table.
 *
 * Index construction (standard ACI): 1-9 primaries/grays, 10-249 = 24 hue
 * rows × 10 shades (row base full color; shades 1-4 darken by 20% steps,
 * 5-9 lighten toward white in 1/6 steps), 250-255 = fixed grays.
 */

export const ACI_BYBLOCK = 0;
export const ACI_BYLAYER = 256;

/** ACI 1-9: primaries + gray ramp. */
const BASE_COLORS: Record<number, [number, number, number]> = {
    1: [255, 0, 0],
    2: [255, 255, 0],
    3: [0, 255, 0],
    4: [0, 255, 255],
    5: [0, 0, 255],
    6: [255, 0, 255],
    7: [255, 255, 255],
    8: [128, 128, 128],
    9: [192, 192, 192],
};

/** Base hues of the 24 shade rows (ACI 10, 20, … 240). */
const HUE_BASES: [number, number, number][] = [
    [255, 0, 0], [255, 63, 0], [255, 127, 0], [255, 191, 0],
    [255, 255, 0], [191, 255, 0], [127, 255, 0], [63, 255, 0],
    [0, 255, 0], [0, 255, 63], [0, 255, 127], [0, 255, 191],
    [0, 255, 255], [0, 191, 255], [0, 127, 255], [0, 63, 255],
    [0, 0, 255], [63, 0, 255], [127, 0, 255], [191, 0, 255],
    [255, 0, 255], [255, 0, 191], [255, 0, 127], [255, 0, 63],
];

/** ACI 250-255 fixed grays. */
const GRAYS: [number, number, number][] = [
    [51, 51, 51], [80, 80, 80], [105, 105, 105],
    [130, 130, 130], [190, 190, 190], [216, 216, 216],
];

function buildAciTable(): [number, number, number][] {
    // Index 0 is a placeholder (BYBLOCK has no color of its own).
    const table: [number, number, number][] = [[0, 0, 0]];
    for (let aci = 1; aci <= 9; aci++) table.push(BASE_COLORS[aci]);
    for (let row = 0; row < 24; row++) {
        const [r, g, b] = HUE_BASES[row];
        for (let shade = 0; shade < 10; shade++) {
            if (shade < 5) {
                const f = 1 - shade * 0.2;
                table.push([Math.round(r * f), Math.round(g * f), Math.round(b * f)]);
            } else {
                const f = (shade - 4) / 6;
                table.push([
                    Math.round(r + (255 - r) * f),
                    Math.round(g + (255 - g) * f),
                    Math.round(b + (255 - b) * f),
                ]);
            }
        }
    }
    GRAYS.forEach((g) => table.push(g));
    return table;
}

/** Standard 255-entry ACI table, indexed 0-255 (0 = placeholder). */
export const ACI_TABLE: readonly [number, number, number][] = buildAciTable();

/** '#rrggbb' → [r,g,b]; null when the value is not a 6-digit hex color. */
export function hexToRgb(hex: unknown): [number, number, number] | null {
    if (typeof hex !== 'string' || !hex.startsWith('#')) return null;
    const h = hex.length === 4
        ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
        : hex;
    if (h.length !== 7) return null;
    const r = parseInt(h.slice(1, 3), 16), g = parseInt(h.slice(3, 5), 16), b = parseInt(h.slice(5, 7), 16);
    if ([r, g, b].some((v) => !Number.isFinite(v))) return null;
    return [r, g, b];
}

/**
 * Nearest standard ACI for a hex color (squared-RGB distance over the full
 * palette). Exact palette colors resolve to their own index — the classic
 * '#ff0000'→1, '#ffff00'→2, … primaries are bit-exact.
 */
export function colorToAci(hex: unknown): number {
    const rgb = hexToRgb(hex);
    if (!rgb) return ACI_BYLAYER;
    let best = 7, bestDist = Infinity;
    for (let aci = 1; aci <= 255; aci++) {
        const [r, g, b] = ACI_TABLE[aci];
        const dr = r - rgb[0], dg = g - rgb[1], db = b - rgb[2];
        const d = dr * dr + dg * dg + db * db;
        if (d < bestDist) { bestDist = d; best = aci; }
    }
    return best;
}

/** ACI → '#rrggbb' (black for the BYBLOCK/BYLAYER placeholders). */
export function aciToHex(aci: number): string {
    const [r, g, b] = ACI_TABLE[Math.min(255, Math.max(1, Math.round(aci)))];
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** ACI → packed 0xRRGGBB int for CTB grammar comparisons. */
export function aciToPacked(aci: number): number {
    const [r, g, b] = ACI_TABLE[Math.min(255, Math.max(1, Math.round(aci)))];
    return (r << 16) | (g << 8) | b;
}
